/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/**
 * Moves all child elements from a host element to a target element using a document fragment.
 * This is useful for efficiently transferring DOM nodes between elements while preserving their order.
 *
 * @param host - The element whose children will be moved
 * @param target - The element that will receive the children
 */
export function adoptLightChildren(host: HTMLElement, target: HTMLElement) {
  const frag = document.createDocumentFragment();
  while (host.firstChild) {
    frag.appendChild(host.firstChild);
  }
  target.appendChild(frag);
}

/**
 * Conditionally attaches children from a wrapper element to a target element.
 * If a wrapper is provided, all its children are moved to the target element.
 * If no wrapper is provided, no action is taken.
 *
 * @param element - The target element that will receive the children
 * @param wrapper - Optional wrapper element whose children will be moved. If undefined, no action is taken.
 */
export function attachChildren(element: HTMLElement, wrapper?: HTMLDivElement) {
  if (wrapper) adoptLightChildren(element, wrapper);
}

/**
 * Merges multiple ref callbacks into a single ref callback function.
 * This is useful when you need to apply multiple refs to the same element,
 * such as combining forwardRef with local refs.
 *
 * @template T - The type of element the refs will be applied to
 * @param refs - Variable number of ref callbacks. Each callback receives the element and can be undefined.
 * @returns A single ref callback that applies all provided refs to the element
 *
 * @example
 * ```tsx
 * const ref1 = useRef<HTMLDivElement>(null);
 * const ref2 = useRef<HTMLDivElement>(null);
 *
 * return (
 *   <div ref={mergeRefs(ref1, ref2, forwardRef)} />
 * );
 * ```
 */
export function mergeRefs<T extends Element>(
  ...refs: (((el: T) => void) | undefined)[]
) {
  return (el: T) => {
    refs.forEach((ref) => ref?.(el));
  };
}
